import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { oktaAuth } from '../../authentication/OktaSSO'
import { getRequestToken, handleProtectedUnauthorized } from '../httpAuth'
import {getClientId} from '../../authentication/clientIdService'

const getBaseApiUrl = () => {
  const currentLocation = window.location.hostname

  switch (currentLocation) {
    case 'localhost': {

       return 'https://planner365.bss.dev.pub.gcp'
      // return 'http://localhost'
    }
    default: {

      return `https://${currentLocation}`
    }
  }
}

const httpServiceInstance = axios.create()
axios.defaults.headers.common['x-api-version'] = '1.0'

const requireAuthentication = (config: AxiosRequestConfig): boolean => {
  if (config.url?.includes('Book/ByQuery') ||
      config.url?.includes('SelectValues/EmployeeDetails') ||
      config.url?.includes('desk-booking/api/book') ||
      config.url?.includes('desk-booking/api/CheckInOut')) {
    return false
  }
  return true
}

httpServiceInstance.interceptors.request.use(
  async config => {
    const token = await getRequestToken(oktaAuth, config, requireAuthentication)

    // Create headers object with index signature to allow dynamic properties
    const headers: Record<string, string | number> = {
      Authorization: `Bearer ${token ?? ''}`,
      Accept: 'application/json',
      'x-api-version': 1.0,
    }

    // Only add x-client-id if it's not null
    const clientId = getClientId()
    if (clientId !== null) {
      headers['x-client-id'] = clientId
    }

    // eslint-disable-next-line no-param-reassign
    config.headers = headers
    
    return config
  },
  error => {
    Promise.reject(error)
  }
)

httpServiceInstance.interceptors.response.use(
  response => response,
  error => {
    handleProtectedUnauthorized(error, requireAuthentication)
    return Promise.reject(error)
  },
)

const response = <T>(axiosResponse: AxiosResponse<T>): T => axiosResponse.data

const buildUrl = (partialUrl: string)=>{
  const currentLocation = window.location.hostname
  const deskBookingUrl = `${getBaseApiUrl()}${partialUrl}`
  const localBookingApiUrl = `${getBaseApiUrl()}:8058${partialUrl}`
  const localDeskBookingApiUrl = `${getBaseApiUrl()}:8059${partialUrl}`

  if (!currentLocation.includes('localhost') || !getBaseApiUrl().includes('localhost')) {
    return deskBookingUrl
  }
  
  return partialUrl.includes('desk-booking') && getBaseApiUrl().includes('localhost') ? localDeskBookingApiUrl : localBookingApiUrl
}

const httpService = {
  get: <T>(url: string, config?: AxiosRequestConfig) => httpServiceInstance.get<T>(buildUrl(url), config).then(response),
  post: <T>(url: string, body?: unknown) => httpServiceInstance.post<T>(buildUrl(url), body).then(response),
  put: <T>(url: string, body: unknown) => httpServiceInstance.put<T>(buildUrl(url), body).then(response),
  patch: <T>(url: string, body: unknown) => httpServiceInstance.patch<T>(buildUrl(url), body).then(response),
  del: (url: string) => httpServiceInstance.delete(buildUrl(url)).then(response),
  delWithBody: (url: string, body: unknown) =>
    httpServiceInstance.delete(url, { data: body }).then(response),
}

export default httpService
